import { Venue } from "@/graphql/operations";
import { Menu, MenuItem } from "@mui/material";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

type VenueOptionsMenuProps = {
  isMenuOpen: boolean;
  handleClose: () => void;
  venueOptions: Pick<Venue, "id" | "name">[];
  loadingButtonContainerRef?: MutableRefObject<HTMLDivElement | null>;
  buttonGroupRef?: MutableRefObject<HTMLDivElement | null>;
  handleSelectOption: (venueId: Venue["id"]) => Promise<void>;
};
export function VenueOptionsMenu({
  venueOptions,
  handleClose,
  handleSelectOption,
  isMenuOpen,
  buttonGroupRef,
  loadingButtonContainerRef,
}: VenueOptionsMenuProps) {
  const [menuMinWidth, setMenuMinWidth] = useState<number>();

  const updateMenuMinWidth = useCallback(() => {
    const loadingEl = loadingButtonContainerRef?.current;
    if (!loadingEl) return;

    const loadingButtonWidth = loadingEl.getBoundingClientRect().width;
    const buttonGroupWidth =
      buttonGroupRef?.current?.getBoundingClientRect().width ??
      loadingButtonWidth;

    const minRequiredLoadingButtonWidthPx = 200 as const;

    setMenuMinWidth(
      loadingButtonWidth < minRequiredLoadingButtonWidthPx
        ? Math.max(loadingButtonWidth, buttonGroupWidth)
        : loadingButtonWidth
    );
  }, [buttonGroupRef, loadingButtonContainerRef]);

  useLayoutEffect(() => {
    updateMenuMinWidth();
  }, [updateMenuMinWidth]);

  useEffect(() => {
    const loadingEl = loadingButtonContainerRef?.current;
    if (!loadingEl) return;

    const resizeObserver = new ResizeObserver(() => updateMenuMinWidth());
    resizeObserver.observe(loadingEl);

    const groupEl = buttonGroupRef?.current;
    if (groupEl) resizeObserver.observe(groupEl);

    return () => resizeObserver.disconnect();
  }, [updateMenuMinWidth, buttonGroupRef, loadingButtonContainerRef]);

  return (
    <Menu
      anchorEl={loadingButtonContainerRef?.current}
      open={isMenuOpen}
      onClose={handleClose}
      slotProps={{
        paper: { sx: { minWidth: menuMinWidth } },
      }}
    >
      {venueOptions.map((venue) => (
        <MenuItem
          key={venue.id}
          value={venue.id}
          onClick={() => handleSelectOption(venue.id)}
        >
          {venue.name}
        </MenuItem>
      ))}
    </Menu>
  );
}
